import { COMMON_TIMEZONES } from './tzUtils';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import './AdminAccount.css';
import { ORDER_TYPE_PRESETS, inferOrderType, normalizeOrderTypeKey } from './orderTypeUtils';
import DeliveryZoneMap from './DeliveryZoneMap';

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Fallback tags if Firestore collection is not yet seeded
const FALLBACK_TAGS = [
  // Cuisines
  { slug: 'burgers', label: 'Burgers', category: 'cuisines' },
  { slug: 'pizza', label: 'Pizza', category: 'cuisines' },
  { slug: 'sushi', label: 'Sushi', category: 'cuisines' },
  { slug: 'tacos', label: 'Tacos', category: 'cuisines' },
  { slug: 'pastas', label: 'Pastas', category: 'cuisines' },
  { slug: 'wings', label: 'Wings', category: 'cuisines' },
  { slug: 'salads', label: 'Salads', category: 'cuisines' },
  { slug: 'sandwiches', label: 'Sandwiches / Sub', category: 'cuisines' },
  { slug: 'desserts', label: 'Desserts / Ice Cream', category: 'cuisines' },
  { slug: 'coffee', label: 'Coffee & Bakery', category: 'cuisines' },
  { slug: 'seafood', label: 'Seafood', category: 'cuisines' },
  { slug: 'chicken', label: 'Fried / Roasted Chicken', category: 'cuisines' },
  // Nationalities
  { slug: 'salvadoran', label: 'Salvadoran', category: 'nationalities' },
  { slug: 'mexican', label: 'Mexican', category: 'nationalities' },
  { slug: 'italian', label: 'Italian', category: 'nationalities' },
  { slug: 'asian', label: 'Asian', category: 'nationalities' },
  { slug: 'vietnamese', label: 'Vietnamese', category: 'nationalities' },
  { slug: 'chinese', label: 'Chinese', category: 'nationalities' },
  { slug: 'japanese', label: 'Japanese', category: 'nationalities' },
  { slug: 'american', label: 'American', category: 'nationalities' },
  { slug: 'mediterranean', label: 'Mediterranean', category: 'nationalities' },
  { slug: 'peruvian', label: 'Peruvian', category: 'nationalities' },
  { slug: 'venezuelan', label: 'Venezuelan / Colombian', category: 'nationalities' },
  // Lifestyle / Dietary
  { slug: 'healthy', label: 'Healthy', category: 'lifestyle' },
  { slug: 'vegetarian', label: 'Vegetarian', category: 'lifestyle' },
  { slug: 'vegan', label: 'Vegan', category: 'lifestyle' },
  { slug: 'gluten-free', label: 'Gluten-Free', category: 'lifestyle' },
  { slug: 'keto', label: 'Keto', category: 'lifestyle' },
  { slug: 'organic', label: 'Organic / Eco', category: 'lifestyle' },
  // Time of day
  { slug: 'breakfast', label: 'Breakfast', category: 'time' },
  { slug: 'brunch', label: 'Brunch', category: 'time' },
  { slug: 'lunch', label: 'Lunch', category: 'time' },
  { slug: 'dinner', label: 'Dinner', category: 'time' },
  { slug: 'late-night', label: 'Late Night', category: 'time' },
  // Service model
  { slug: 'fast-food', label: 'Fast Food', category: 'service' },
  { slug: 'casual-dining', label: 'Casual Dining', category: 'service' },
  { slug: 'fine-dining', label: 'Fine Dining / Gourmet', category: 'service' },
  { slug: 'bakery', label: 'Bakery / Pastry', category: 'service' },
  { slug: 'drinks', label: 'Drinks / Smoothies', category: 'service' },
];

const defaultOperationHours = [
  { day: 'monday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'tuesday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'wednesday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'thursday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'friday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'saturday', fromHour: 9, toHour: 17, isClosed: false, isNextDay: false },
  { day: 'sunday', fromHour: 0, toHour: 0, isClosed: true, isNextDay: false },
];

const formatHour12 = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const LocationMaintenancePage = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  
  const [formData, setFormData] = useState({
    locationName: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    websiteUrl: '',
    taxRate: '',
    // Required — reports/Day Close bucket by this. Pre-filled from the
    // browser on create; the merchant can change it.
    timezone: (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '') || '',
    convenienceFeeEnabled: false,
    convenienceFeePercent: '3',
    reservationSlotInterval: '30',
    reservationDuration: '90',
    reservationMinAdvanceHours: '2',
    reservationMaxAdvanceDays: '30',
    reservationMaxPartySize: '12',
    reservationMaxReservationsPerSlot: '10',
    reservationAutoNoShowMinutes: '30',
    reservationRequireConfirmation: false,
    queueEnabled: false,
    deliveryUberEnabled: true,
    // Delivery zone settings (deliverySettings in Firestore). zoneType:
    // 'radius' (miles around the restaurant) | 'zipcodes' (explicit list).
    // 5 miles is the industry-standard default for restaurant delivery
    // (DoorDash/Uber Eats typically quote 3–5 mi).
    deliveryZoneType: 'radius',
    deliveryRadiusMiles: '5',
    deliveryZipCodes: [],
    deliveryZipInput: '',
    queueAvgTurnMinutes: '45',
    queueMaxQueueSize: '50',
    pointsPerDollar: '',
    enableFodyesRewards: false,
    fodyesCoinValue: '',
    fodyesReturnPercentage: '',
    maxFodyesDiscount: '',
    minOrderForFodyes: '',
    acceptCash: true,
    // Cash acceptance per sales channel — replaces the single global toggle
    // (e.g. a restaurant may take cash at the POS drawer but CC-only at the
    // kiosk). Defaults all-on; a legacy location with acceptCash === false
    // loads all-off (see load below).
    acceptCashChannels: {
      pos: true,
      online: true,
      kiosk: true,
      handheld: true,
      reservation: true,
    },
    stayTabsOpen: false,
    confirmAllOrders: false,
    ordersCorrelative: 10,
    defaultFulfillmentStatus: 'pending',
    cashFulfillmentStatus: 'pending',
    operationHours: [...defaultOperationHours],
    latitude: null,
    longitude: null,
    logoUrl: '',
    tags: [],
    coverImages: [],
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);
  
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [orderTypes, setOrderTypes] = useState([]);
  const [showOrderTypesModal, setShowOrderTypesModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);

  // Reservations are driven by the "Dine In" order type — it is the single
  // master switch. There is intentionally NO separate "Accept Reservations"
  // toggle; reservationSettings.enabled is derived from (and kept in sync
  // with) the dine_in order type so the two can never disagree.
  const dineInEnabled = orderTypes.some((t) => t.key === 'dine_in' && t.enabled);

  // Delivery settings follow the same master-switch pattern: the "Delivery"
  // order type gates the whole Delivery section below (zone, fulfillment).
  const deliveryEnabled = orderTypes.some((t) => t.key === 'delivery' && t.enabled);

  // Stripe Terminal tipping configuration
  const [stripeLocationId, setStripeLocationId] = useState(null);
  const [stripeConfigurationId, setStripeConfigurationId] = useState(null);
  const [stripeTippingEnabled, setStripeTippingEnabled] = useState(false);
  const [stripeTippingPercentages, setStripeTippingPercentages] = useState([15, 18, 20]); // Stripe Terminal supports exactly 3 percentages
  const [stripeTippingLoading, setStripeTippingLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (isEditMode) {
          await loadLocation(currentUser.uid, id);
        } else {
          setLoading(false);
        }
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  // Load available tags from Firestore
  useEffect(() => {
    const loadTags = async () => {
      try {
        const db = getFirestore();
        const tagsSnap = await getDocs(collection(db, 'tags'));
        if (tagsSnap.empty) {
          setAvailableTags(FALLBACK_TAGS);
          return;
        }
        const tags = tagsSnap.docs
          .map((d) => {
            const data = d.data();
            return { slug: data.slug || d.id, label: data.label || d.id, category: data.category || 'cuisines' };
          })
          .sort((a, b) => {
            const orderA = tagsSnap.docs.find((d) => d.id === a.slug)?.data()?.order ?? 0;
            const orderB = tagsSnap.docs.find((d) => d.id === b.slug)?.data()?.order ?? 0;
            return orderA - orderB;
          });
        setAvailableTags(tags);
      } catch (err) {
        console.error('Error loading tags from Firestore, using fallback:', err);
        setAvailableTags(FALLBACK_TAGS);
      }
    };
    loadTags();
  }, []);

  const loadLocation = async (uid, locationId) => {
    try {
      const db = getFirestore();
      const docSnap = await getDoc(doc(db, 'users', uid, 'locations', locationId));
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Load order types (normalized — legacy `{name}`-only docs get a
        // stable key + enabled=true inferred on read).
        const orderTypesSnapshot = await getDocs(collection(db, 'users', uid, 'locations', locationId, 'orderTypes'));
        const types = orderTypesSnapshot.docs.map(doc => inferOrderType(doc.id, doc.data()));
        setOrderTypes(types);
        
        setFormData({
          locationName: data.companyName || '',
          description: data.description || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          country: data.country || '',
          phone: data.phone || '',
          websiteUrl: data.websiteUrl || '',
          taxRate: data.taxRate > 0 ? data.taxRate.toString() : '',
          timezone: data.timezone || '',
          convenienceFeeEnabled: data.convenienceFee?.enabled === true,
          convenienceFeePercent:
            data.convenienceFee?.percent > 0 ? data.convenienceFee.percent.toString() : '3',
          reservationSlotInterval: (data.reservationSettings?.slotIntervalMinutes ?? 30).toString(),
          reservationDuration: (data.reservationSettings?.defaultDurationMinutes ?? 90).toString(),
          reservationMinAdvanceHours: (data.reservationSettings?.minAdvanceHours ?? 2).toString(),
          reservationMaxAdvanceDays: (data.reservationSettings?.maxAdvanceDays ?? 30).toString(),
          reservationMaxPartySize: (data.reservationSettings?.maxPartySize ?? 12).toString(),
          reservationMaxReservationsPerSlot: (data.reservationSettings?.maxReservationsPerSlot ?? 10).toString(),
          reservationAutoNoShowMinutes: (data.reservationSettings?.autoNoShowMinutes ?? 30).toString(),
          reservationRequireConfirmation: data.reservationSettings?.requireConfirmation === true,
          queueEnabled: data.queueSettings?.enabled === true,
          deliveryUberEnabled: data.deliveryProvider !== 'none',
          deliveryZoneType: data.deliverySettings?.zoneType === 'zipcodes' ? 'zipcodes' : 'radius',
          deliveryRadiusMiles: (data.deliverySettings?.radiusMiles ?? 5).toString(),
          deliveryZipCodes: Array.isArray(data.deliverySettings?.zipCodes)
            ? data.deliverySettings.zipCodes.map((z) => String(z))
            : [],
          deliveryZipInput: '',
          queueAvgTurnMinutes: (data.queueSettings?.avgTurnMinutes ?? 45).toString(),
          queueMaxQueueSize: (data.queueSettings?.maxQueueSize ?? 50).toString(),
          pointsPerDollar: data.pointsPerDollar > 0 ? data.pointsPerDollar.toString() : '',
          enableFodyesRewards: data.enableFodyesRewards === true,
          fodyesCoinValue: data.fodyesCoinValue > 0 ? data.fodyesCoinValue.toString() : '',
          fodyesReturnPercentage: data.fodyesReturnPercentage > 0 ? data.fodyesReturnPercentage.toString() : '',
          maxFodyesDiscount: data.maxFodyesDiscount > 0 ? data.maxFodyesDiscount.toString() : '',
          minOrderForFodyes: data.minOrderForFodyes > 0 ? data.minOrderForFodyes.toString() : '',
          acceptCash: data.acceptCash !== false,
          // Per-channel map with legacy fallback: locations saved before this
          // field existed resolve each channel from the legacy global toggle.
          acceptCashChannels: data.acceptCashByChannel != null
            ? {
                pos: data.acceptCashByChannel.pos !== false,
                online: data.acceptCashByChannel.online !== false,
                kiosk: data.acceptCashByChannel.kiosk !== false,
                handheld: data.acceptCashByChannel.handheld !== false,
                reservation: data.acceptCashByChannel.reservation !== false,
              }
            : {
                pos: data.acceptCash !== false,
                online: data.acceptCash !== false,
                kiosk: data.acceptCash !== false,
                handheld: data.acceptCash !== false,
                reservation: data.acceptCash !== false,
              },
          stayTabsOpen: data.stayTabsOpen === true,
          confirmAllOrders: data.confirmAllOrders === true,
          ordersCorrelative: data.ordersCorrelative || 10,
          defaultFulfillmentStatus: data.defaultFulfillmentStatus || 'pending',
          cashFulfillmentStatus: data.cashFulfillmentStatus || 'pending',
          operationHours: data.operationHours || [...defaultOperationHours],
          latitude: data.latitude || null,
          longitude: data.longitude || null,
          logoUrl: data.logoUrl || '',
          tags: data.tags || [],
          coverImages: data.coverImages || [],
        });

        // Load Stripe Terminal tipping config from Firestore
        if (data.terminalTipping) {
          setStripeTippingEnabled(data.terminalTipping.enableTips === true);
          if (data.terminalTipping.percentages && data.terminalTipping.percentages.length > 0) {
            setStripeTippingPercentages(data.terminalTipping.percentages);
          }
        }
        if (data.stripeLocationId) {
          setStripeLocationId(data.stripeLocationId);
          setStripeConfigurationId(data.stripeConfigurationId || null);
        }
      } else {
        Swal.fire('Error', 'Location not found', 'error');
        navigate('/admin/locations');
      }
    } catch (error) {
      console.error('Error loading location:', error);
      Swal.fire('Error', 'Failed to load location', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchAddress = async (query) => {
    if (!query || query.length < 3 || !MAPBOX_ACCESS_TOKEN) return;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&limit=5`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Mapbox search error:', error);
    }
  };

  const normalizeCountryCode = (country) => {
    if (!country) return 'US';
    const c = country.trim();
    if (c.length === 2 && /^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
    const map = {
      'united states': 'US',
      'united states of america': 'US',
      'usa': 'US',
      'canada': 'CA',
      'mexico': 'MX',
      'united kingdom': 'GB',
      'great britain': 'GB',
      'france': 'FR',
      'germany': 'DE',
      'spain': 'ES',
      'italy': 'IT',
      'australia': 'AU',
      'brazil': 'BR',
      'india': 'IN',
      'japan': 'JP',
      'china': 'CN',
      'south korea': 'KR',
      'netherlands': 'NL',
      'belgium': 'BE',
      'switzerland': 'CH',
      'austria': 'AT',
      'sweden': 'SE',
      'norway': 'NO',
      'denmark': 'DK',
      'finland': 'FI',
      'ireland': 'IE',
      'portugal': 'PT',
      'greece': 'GR',
      'poland': 'PL',
      'czech republic': 'CZ',
      'hungary': 'HU',
      'romania': 'RO',
      'bulgaria': 'BG',
      'croatia': 'HR',
      'slovenia': 'SI',
      'slovakia': 'SK',
      'lithuania': 'LT',
      'latvia': 'LV',
      'estonia': 'EE',
      'luxembourg': 'LU',
      'malta': 'MT',
      'cyprus': 'CY',
    };
    const lower = c.toLowerCase();
    if (map[lower]) return map[lower];
    return c;
  };

  const selectAddress = (place) => {
    const [longitude, latitude] = place.center || [null, null];
    const context = place.context || [];
    
    // Parse context for city, state, country, zip
    let city = '';
    let state = '';
    let country = '';
    let zip = '';
    
    context.forEach(item => {
      if (item.id.startsWith('place.')) city = item.text;
      if (item.id.startsWith('region.')) state = item.text;
      if (item.id.startsWith('country.')) {
        country = item.short_code || item.text;
      }
      if (item.id.startsWith('postcode.')) zip = item.text;
    });

    setFormData({
      ...formData,
      address: place.place_name,
      city,
      state,
      zip,
      country: normalizeCountryCode(country),
      latitude,
      longitude,
    });
    setShowAddressSuggestions(false);
  };

  const handleChange = (field, value) => {
    // Cash channel toggles are namespaced ('cash_<channel>') and edit the
    // nested acceptCashChannels map instead of a flat field.
    if (typeof field === 'string' && field.startsWith('cash_')) {
      const channel = field.slice(5);
      setFormData({
        ...formData,
        acceptCashChannels: { ...formData.acceptCashChannels, [channel]: value },
      });
      return;
    }
    setFormData({ ...formData, [field]: value });
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setLogoUploading(true);
    try {
      const storage = getStorage();
      const userName = user.displayName?.toLowerCase().replace(/\s+/g, '-') || 'user';
      const folderPath = `${userName}-${user.uid}/locations`;
      const storageRef = ref(storage, `${folderPath}/${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, logoUrl: downloadUrl }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      Swal.fire('Error', 'Failed to upload logo', 'error');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleCoverImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !user) return;

    setCoverUploading(true);
    const uploaded = [];
    try {
      const storage = getStorage();
      const userName = user.displayName?.toLowerCase().replace(/\s+/g, '-') || 'user';
      const folderPath = `user-${user.uid}/location/cover`;

      for (const file of files) {
        const storageRef = ref(storage, `${folderPath}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        uploaded.push(downloadUrl);
      }

      setFormData(prev => ({ ...prev, coverImages: [...prev.coverImages, ...uploaded] }));
    } catch (error) {
      console.error('Error uploading cover images:', error);
      Swal.fire('Error', 'Failed to upload one or more cover images', 'error');
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleRemoveCoverImage = async (index) => {
    const url = formData.coverImages[index];
    if (url) {
      try {
        const storage = getStorage();
        // Extract storage path from the public download URL
        const match = url.match(/\/o\/(.+)\?alt=media/);
        if (match) {
          const path = decodeURIComponent(match[1]);
          await deleteObject(ref(storage, path));
        }
      } catch (err) {
        console.error('Failed to delete cover image from storage:', err);
        // Don’t block UI removal even if Storage deletion fails
      }
    }
    setFormData(prev => ({
      ...prev,
      coverImages: prev.coverImages.filter((_, i) => i !== index),
    }));
  };

  const handlePhoneChange = (value) => {
    const digits = value.replace(/[^\d]/g, '').substring(0, 10);
    let formatted = '';
    for (let i = 0; i < digits.length; i++) {
      if (i === 0) formatted += '(';
      if (i === 3) formatted += ') ';
      if (i === 6) formatted += '-';
      formatted += digits[i];
    }
    handleChange('phone', formatted);
  };

  // ── Tags helpers ──
  const toggleTag = (slug) => {
    const current = formData.tags || [];
    const normalized = slug.trim().toLowerCase();
    if (current.includes(normalized)) {
      handleChange('tags', current.filter((t) => t !== normalized));
    } else {
      handleChange('tags', [...current, normalized]);
    }
  };

  const addCustomTag = (raw) => {
    const val = raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    if (!val) return;
    const current = formData.tags || [];
    if (!current.includes(val)) {
      handleChange('tags', [...current, val]);
    }
  };

  const removeTag = (slug) => {
    const current = formData.tags || [];
    handleChange('tags', current.filter((t) => t !== slug));
  };

  const validateForm = () => {
    if (!formData.locationName.trim()) {
      Swal.fire('Error', 'Location name is required', 'error');
      return false;
    }
    if (formData.locationName.trim().length < 2) {
      Swal.fire('Error', 'Location name must be at least 2 characters', 'error');
      return false;
    }
    // Required for the platform to work: Stripe Terminal pairing, delivery
    // pickup quotes and the public store all need a complete address.
    const missing = [];
    if (!formData.address.trim()) missing.push('Address');
    if (!formData.city.trim()) missing.push('City');
    if (!formData.state.trim()) missing.push('State');
    if (!formData.zip.trim()) missing.push('Zip Code');
    if (!formData.country.trim()) missing.push('Country');
    if (!formData.timezone.trim()) missing.push('Timezone');
    if (missing.length > 0) {
      Swal.fire('Missing required fields', `${missing.join(', ')} — needed for terminal pairing, delivery and accurate reports.`, 'error');
      return false;
    }
    if (formData.phone) {
      const digits = formData.phone.replace(/[^\d]/g, '');
      if (digits.length > 0 && digits.length < 10) {
        Swal.fire('Error', 'Phone must be at least 10 digits', 'error');
        return false;
      }
    }
    if (formData.taxRate) {
      const rate = parseFloat(formData.taxRate);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        Swal.fire('Error', 'Tax rate must be between 0 and 100', 'error');
        return false;
      }
    }
    if (formData.convenienceFeeEnabled) {
      const feeRate = parseFloat(formData.convenienceFeePercent);
      if (isNaN(feeRate) || feeRate < 0 || feeRate > 100) {
        Swal.fire('Error', 'Convenience Fee must be between 0 and 100', 'error');
        return false;
      }
    }
    if (formData.enableFodyesRewards) {
      const coinVal = parseFloat(formData.fodyesCoinValue);
      if (isNaN(coinVal) || coinVal <= 0) {
        Swal.fire('Error', 'Fodyes Coin Value must be greater than 0', 'error');
        return false;
      }
      const retPct = parseFloat(formData.fodyesReturnPercentage);
      if (isNaN(retPct) || retPct <= 0 || retPct > 100) {
        Swal.fire('Error', 'Fodyes Return % must be between 0 and 100', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;
    
    try {
      setSaving(true);
      const db = getFirestore();
      
      const locationData = {
        companyName: formData.locationName.trim(),
        description: formData.description.trim() || null,
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        zip: formData.zip.trim() || null,
        country: formData.country.trim() || null,
        phone: formData.phone.trim() || null,
        // Legacy master = true while at least one channel is on (keeps old
        // readers working); the per-channel map is the granular layer.
        acceptCash: Object.values(formData.acceptCashChannels).some((v) => v === true),
        acceptCashByChannel: formData.acceptCashChannels,
        stayTabsOpen: formData.stayTabsOpen,
        confirmAllOrders: formData.confirmAllOrders,
        taxRate: parseFloat(formData.taxRate) || 0,
        timezone: formData.timezone || null,
        convenienceFee: {
          enabled: formData.convenienceFeeEnabled,
          percent: parseFloat(formData.convenienceFeePercent) || 0,
        },
        reservationSettings: {
          // Derived from the Dine In order type (single master switch) so
          // reservations can never be "on" while Dine In is off (or vice
          // versa). See dineInEnabled above.
          enabled: dineInEnabled,
          slotIntervalMinutes: parseInt(formData.reservationSlotInterval) || 30,
          defaultDurationMinutes: parseInt(formData.reservationDuration) || 90,
          minAdvanceHours: parseInt(formData.reservationMinAdvanceHours) || 2,
          maxAdvanceDays: parseInt(formData.reservationMaxAdvanceDays) || 30,
          maxPartySize: parseInt(formData.reservationMaxPartySize) || 12,
          maxReservationsPerSlot: parseInt(formData.reservationMaxReservationsPerSlot) || 10,
          autoNoShowMinutes: parseInt(formData.reservationAutoNoShowMinutes) || 30,
          requireConfirmation: formData.reservationRequireConfirmation,
        },
        queueSettings: {
          enabled: formData.queueEnabled,
          avgTurnMinutes: parseInt(formData.queueAvgTurnMinutes) || 45,
          maxQueueSize: parseInt(formData.queueMaxQueueSize) || 50,
        },
        // Delivery fulfillment provider — default-ON (Uber Direct is the
        // only 3PL for now); 'none' opts the location out (manual delivery).
        deliveryProvider: formData.deliveryUberEnabled ? 'uber_direct' : 'none',
        // Delivery zone — enforced server-side in getUberDeliveryQuote and
        // client-side in the public checkouts.
        deliverySettings: {
          zoneType: formData.deliveryZoneType === 'zipcodes' ? 'zipcodes' : 'radius',
          radiusMiles: parseFloat(formData.deliveryRadiusMiles) > 0 ? parseFloat(formData.deliveryRadiusMiles) : 5,
          zipCodes: formData.deliveryZipCodes,
        },
        pointsPerDollar: parseFloat(formData.pointsPerDollar) || 0,
        enableFodyesRewards: formData.enableFodyesRewards,
        fodyesCoinValue: parseFloat(formData.fodyesCoinValue) || 0,
        fodyesReturnPercentage: parseFloat(formData.fodyesReturnPercentage) || 0,
        maxFodyesDiscount: parseFloat(formData.maxFodyesDiscount) || 0,
        minOrderForFodyes: parseFloat(formData.minOrderForFodyes) || 0,
        ordersCorrelative: formData.ordersCorrelative,
        defaultFulfillmentStatus: formData.defaultFulfillmentStatus || 'pending',
        cashFulfillmentStatus: formData.cashFulfillmentStatus || 'pending',
        websiteUrl: formData.websiteUrl.trim() || null,
        operationHours: formData.operationHours,
        latitude: formData.latitude,
        longitude: formData.longitude,
        logoUrl: formData.logoUrl || null,
        tags: (formData.tags || []).map((t) => t.trim().toLowerCase()).filter(Boolean),
        coverImages: formData.coverImages || [],
      };

      if (isEditMode) {
        locationData.updatedAt = serverTimestamp();
        await updateDoc(doc(db, 'users', user.uid, 'locations', id), locationData);

        // If a Stripe Terminal location is linked, update the logo there too
        if (stripeLocationId) {
          try {
            const functions = getFunctions();
            const updateStripeTerminalLocationLogoFn = httpsCallable(functions, 'updateStripeTerminalLocationLogo');
            await updateStripeTerminalLocationLogoFn({
              stripeLocationId,
              stripeConfigurationId,
              logoUrl: formData.logoUrl || null,
              locationId: id,
            });
          } catch (stripeError) {
            console.error('Error updating Stripe Terminal logo:', stripeError);
            // Show warning but don't block location update
            Swal.fire({
              icon: 'warning',
              title: 'Logo update failed',
              text: 'Location was updated, but the terminal logo could not be updated.',
            });
          }
        }
        
        Swal.fire('Success', 'Location updated successfully!', 'success');
      } else {
        locationData.createdAt = serverTimestamp();
        locationData.ordersPosition = 0;
        const docRef = doc(collection(db, 'users', user.uid, 'locations'));
        await setDoc(docRef, locationData);
        
        // Create default order types (new shape: key/enabled/isCustom).
        // delivery + pickup enabled (long-standing default); dine_in stays
        // disabled so Reserve is opt-in per location.
        await setDoc(doc(db, 'users', user.uid, 'locations', docRef.id, 'orderTypes', 'delivery'), { name: 'Delivery', key: 'delivery', enabled: true, isCustom: false });
        await setDoc(doc(db, 'users', user.uid, 'locations', docRef.id, 'orderTypes', 'pickup'), { name: 'Pick Up', key: 'pickup', enabled: true, isCustom: false });
        await setDoc(doc(db, 'users', user.uid, 'locations', docRef.id, 'orderTypes', 'dine_in'), { name: 'Dine In', key: 'dine_in', enabled: false, isCustom: false });
        
        // Auto-select this location as the active one
        await setDoc(doc(db, 'users', user.uid), { selectedLocationId: docRef.id }, { merge: true });
        
        // Create Stripe Terminal location
        try {
          const functions = getFunctions();
          const createStripeTerminalLocationFn = httpsCallable(functions, 'createStripeTerminalLocation');
          await createStripeTerminalLocationFn({
            locationId: docRef.id,
            locationName: formData.locationName.trim(),
            address: formData.address.trim(),
            city: formData.city.trim(),
            state: formData.state.trim(),
            zip: formData.zip.trim(),
            country: normalizeCountryCode(formData.country),
            logoUrl: formData.logoUrl || null,
          });
        } catch (stripeError) {
          console.error('Error creating Stripe Terminal location:', stripeError);
          // Show warning but don't block location creation
          Swal.fire({
            icon: 'warning',
            title: 'Location created',
            text: 'Location was created, but Stripe Terminal location could not be created. You can retry from the locations list.',
          });
          navigate('/admin/locations');
          return;
        }
        
        Swal.fire('Success', 'Location created successfully!', 'success');
      }
      
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      navigate('/admin/locations');
    } catch (error) {
      console.error('Error saving location:', error);
      Swal.fire('Error', error.message || 'Failed to save location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStripeTipping = async () => {
    if (!user || !id) return;
    try {
      setStripeTippingLoading(true);
      const functions = getFunctions();
      const updateTerminalTipsFn = httpsCallable(functions, 'updateTerminalTips');
      await updateTerminalTipsFn({
        locationId: id,
        enableTips: stripeTippingEnabled,
        tipPercentage1: stripeTippingPercentages[0],
        tipPercentage2: stripeTippingPercentages[1],
        tipPercentage3: stripeTippingPercentages[2],
      });
      Swal.fire('Success', 'Terminal tipping configuration saved!', 'success');
    } catch (error) {
      console.error('Error saving terminal tipping config:', error);
      Swal.fire('Error', error.message || 'Failed to save terminal tipping configuration', 'error');
    } finally {
      setStripeTippingLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !id) return;
    const result = await Swal.fire({
      title: 'Delete Location?',
      text: 'Are you sure you want to delete this location? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const db = getFirestore();

        // 1. Delete Stripe Terminal location if linked
        if (stripeLocationId) {
          try {
            const functions = getFunctions();
            const deleteStripeTerminalLocationFn = httpsCallable(functions, 'deleteStripeTerminalLocation');
            await deleteStripeTerminalLocationFn({ stripeLocationId });
          } catch (stripeError) {
            console.error('Error deleting Stripe Terminal location:', stripeError);
            // Don't block deletion if Stripe cleanup fails
          }
        }

        // 2. Delete Firestore location document
        await deleteDoc(doc(db, 'users', user.uid, 'locations', id));

        Swal.fire('Deleted', 'Location has been deleted.', 'success');
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        navigate('/admin/locations');
      } catch (error) {
        console.error('Error deleting location:', error);
        Swal.fire('Error', error.message || 'Failed to delete location', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleStripeTippingPercentageChange = (index, value) => {
    const newPercentages = [...stripeTippingPercentages];
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0 && num <= 100) {
      newPercentages[index] = num;
      setStripeTippingPercentages(newPercentages);
    }
  };

  const handleOperationHoursChange = (index, field, value) => {
    const newHours = [...formData.operationHours];
    newHours[index] = { ...newHours[index], [field]: value };
    setFormData({ ...formData, operationHours: newHours });
  };

  const _refreshOrderTypes = async () => {
    if (!user || !id) return;
    const db = getFirestore();
    const snap = await getDocs(collection(db, 'users', user.uid, 'locations', id, 'orderTypes'));
    setOrderTypes(snap.docs.map((d) => inferOrderType(d.id, d.data())));
  };

  // When the Order Types modal opens, make sure the 3 presets exist as docs
  // (so they can be toggled). delivery/pickup default to enabled (matches the
  // long-standing default seed); dine_in defaults to DISABLED so Reserve is
  // opt-in and doesn't silently appear on every existing location.
  const ensurePresetOrderTypes = async () => {
    if (!user || !id) return;
    const db = getFirestore();
    const existingKeys = new Set(orderTypes.map((t) => t.key));
    const batch = [];
    for (const preset of ORDER_TYPE_PRESETS) {
      if (!existingKeys.has(preset.key)) {
        batch.push(
          setDoc(doc(db, 'users', user.uid, 'locations', id, 'orderTypes', preset.key), {
            name: preset.name,
            key: preset.key,
            enabled: preset.key === 'dine_in' ? false : true,
            isCustom: false,
          })
        );
      }
    }
    if (batch.length > 0) {
      await Promise.all(batch);
      await _refreshOrderTypes();
    }
  };

  // Toggles a preset order type. IMPORTANT: writes to the order type's REAL
  // doc id (`typeId`), NOT the canonical `key`. Legacy docs (e.g. "Pick Up")
  // were created with an id slugged from the name (`pick_up`), which does
  // NOT match the canonical key (`pickup`) — writing to `key` created a
  // duplicate doc and left the original one enabled, which is exactly why
  // Pick Up could never be disabled. Delivery was unaffected because its
  // legacy id already equals its key (`delivery`).
  const handleTogglePreset = async (typeId, enabled) => {
    if (!user || !id) return;
    try {
      const db = getFirestore();
      await setDoc(
        doc(db, 'users', user.uid, 'locations', id, 'orderTypes', typeId),
        { enabled },
        { merge: true }
      );
      // Dine In is the master switch for reservations: keep
      // reservationSettings.enabled in sync immediately so the two never
      // disagree even if the merchant doesn't press "Save Location".
      const toggled = orderTypes.find((t) => t.id === typeId);
      if (toggled?.key === 'dine_in' || typeId === 'dine_in') {
        await setDoc(
          doc(db, 'users', user.uid, 'locations', id),
          { reservationSettings: { enabled } },
          { merge: true }
        );
      }
      setOrderTypes((prev) => prev.map((t) => (t.id === typeId ? { ...t, enabled } : t)));
    } catch (error) {
      console.error('Error toggling order type:', error);
      Swal.fire('Error', 'Failed to update order type', 'error');
    }
  };

  const handleAddOrderType = async () => {
    const { value: name } = await Swal.fire({
      title: 'Add Custom Order Type',
      input: 'text',
      inputLabel: 'Name',
      inputPlaceholder: 'e.g. Catering, Drive Thru...',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please enter a name';
        }
      }
    });

    if (name && user && id) {
      try {
        const db = getFirestore();
        const key = normalizeOrderTypeKey(name);
        await setDoc(doc(db, 'users', user.uid, 'locations', id, 'orderTypes', key), {
          name: name.trim(),
          key,
          enabled: true,
          isCustom: true,
        });
        await _refreshOrderTypes();
        Swal.fire('Success', 'Order type added', 'success');
      } catch (error) {
        console.error('Error adding order type:', error);
        Swal.fire('Error', 'Failed to add order type', 'error');
      }
    }
  };

  const handleEditOrderType = async (typeId, currentName) => {
    const { value: name } = await Swal.fire({
      title: 'Edit Order Type',
      input: 'text',
      inputLabel: 'Name',
      inputValue: currentName,
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please enter a name';
        }
      }
    });

    if (name && user && id) {
      try {
        const db = getFirestore();
        // merge: true so we don't clobber key/enabled/isCustom on edit
        await setDoc(doc(db, 'users', user.uid, 'locations', id, 'orderTypes', typeId), {
          name: name.trim()
        }, { merge: true });
        await _refreshOrderTypes();
        Swal.fire('Success', 'Order type updated', 'success');
      } catch (error) {
        console.error('Error updating order type:', error);
        Swal.fire('Error', 'Failed to update order type', 'error');
      }
    }
  };

  const handleDeleteOrderType = async (typeId, typeName) => {
    const result = await Swal.fire({
      title: 'Delete Order Type?',
      text: `Are you sure you want to delete "${typeName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed && user && id) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'locations', id, 'orderTypes', typeId));
        await _refreshOrderTypes();
        Swal.fire('Deleted', 'Order type deleted', 'success');
      } catch (error) {
        console.error('Error deleting order type:', error);
        Swal.fire('Error', 'Failed to delete order type', 'error');
      }
    }
  };

  // When the Order Types modal opens, make sure the 3 presets exist as docs.
  useEffect(() => {
    if (showOrderTypesModal && isEditMode) {
      ensurePresetOrderTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderTypesModal, isEditMode]);

  if (loading) {
    return (
      <AdminLayout title={isEditMode ? 'Edit Location' : 'Add Location'}>
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? 'Edit Location' : 'Add Location'}>
      <div className="admin-account-page">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header bg-orange">
                <h3 className="card-title">
                  <i className="fas fa-store mr-2"></i>
                  {isEditMode ? 'Edit Location' : 'New Location'}
                </h3>
                <small className="d-block mt-1" style={{ opacity: 0.9 }}>
                  Fields marked * are required — the address powers terminal pairing, delivery quotes and your public store; the timezone drives accurate reports.
                </small>
              </div>
              <div className="card-body">
                {/* Location Name */}
                <div className="form-group">
                  <label>Location Name *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-store"></i></span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.locationName}
                      onChange={(e) => handleChange('locationName', e.target.value)}
                      placeholder="Enter location name"
                    />
                  </div>
                </div>

                {/* Location Logo */}
                <div className="form-group">
                  <label>Location Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="d-none"
                    ref={logoInputRef}
                    onChange={handleLogoSelect}
                  />
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      height: 120,
                      borderRadius: 12,
                      border: '2px dashed #e0e0e0',
                      background: '#f8f9fa',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoUploading ? (
                      <div className="text-center">
                        <img src="/fodyfee_loading.png" alt="Uploading..." style={{ width: 40, height: 40, objectFit: 'contain' }} />
                        <div className="mt-2 text-muted">Uploading...</div>
                      </div>
                    ) : formData.logoUrl ? (
                      <img
                        src={formData.logoUrl}
                        alt="Logo Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="text-center">
                        <i className="fas fa-camera fa-2x text-muted"></i>
                        <div className="mt-2 text-muted">Tap to add logo</div>
                      </div>
                    )}
                  </div>
                  {formData.logoUrl && (
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger"
                        onClick={() => handleChange('logoUrl', '')}
                      >
                        <i className="fas fa-trash mr-1"></i>Remove Logo
                      </button>
                    </div>
                  )}
                </div>

                {/* Cover Images */}
                <div className="form-group">
                  <label>Cover Photos <small className="text-muted">(Recommended: 1200 × 675 px, 16:9, max 5)</small></label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="d-none"
                    ref={coverInputRef}
                    onChange={handleCoverImageSelect}
                  />
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      height: 120,
                      borderRadius: 12,
                      border: '2px dashed #e0e0e0',
                      background: '#f8f9fa',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => coverInputRef.current?.click()}
                  >
                    {coverUploading ? (
                      <div className="text-center">
                        <img src="/fodyfee_loading.png" alt="Uploading..." style={{ width: 40, height: 40, objectFit: 'contain' }} />
                        <div className="mt-2 text-muted">Uploading...</div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <i className="fas fa-images fa-2x text-muted"></i>
                        <div className="mt-2 text-muted">Tap to add cover photos</div>
                      </div>
                    )}
                  </div>
                  {formData.coverImages.length > 0 && (
                    <div className="mt-2" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {formData.coverImages.map((url, idx) => (
                        <div key={idx} style={{ position: 'relative', width: 100, height: 100, borderRadius: 8, overflow: 'hidden' }}>
                          <img src={url} alt={`Cover ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            style={{ position: 'absolute', top: 4, right: 4, padding: '2px 6px', fontSize: 10 }}
                            onClick={() => handleRemoveCoverImage(idx)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Enter location description"
                  />
                </div>

                {/* Address with Mapbox Search */}
                <div className="form-group">
                  <label>Address *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-map-marker-alt"></i></span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.address}
                      onChange={(e) => {
                        handleChange('address', e.target.value);
                        searchAddress(e.target.value);
                      }}
                      placeholder="Search address..."
                    />
                  </div>
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div className="list-group mt-1" style={{ position: 'absolute', zIndex: 1000, width: '100%' }}>
                      {addressSuggestions.map((place, index) => (
                        <button
                          key={index}
                          className="list-group-item list-group-item-action"
                          onClick={() => selectAddress(place)}
                        >
                          <i className="fas fa-map-marker-alt mr-2 text-orange"></i>
                          {place.place_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* City & State */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>State *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.state}
                        onChange={(e) => handleChange('state', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Zip & Country */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Zip Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.zip}
                        onChange={(e) => handleChange('zip', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Country *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="form-group">
                  <label>Phone</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-phone"></i></span>
                    </div>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>

                {/* Website */}
                <div className="form-group">
                  <label>Website URL</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-link"></i></span>
                    </div>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.websiteUrl}
                      onChange={(e) => handleChange('websiteUrl', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                {/* Tax Rate */}
                <div className="form-group">
                  <label>Tax Rate (%)</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-percent"></i></span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.taxRate}
                      onChange={(e) => handleChange('taxRate', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Timezone — drives every report's day boundaries (sales
                    belong to the LOCATION's local day, not the viewer's) */}
                <div className="form-group">
                  <label>Timezone *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-globe-americas"></i></span>
                    </div>
                    <select
                      className="form-control"
                      value={formData.timezone}
                      onChange={(e) => handleChange('timezone', e.target.value)}
                    >
                      <option value="">Auto (device that consults) — not recommended</option>
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleChange('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone || '')}
                        title="Use this browser's timezone"
                      >
                        Detect
                      </button>
                    </div>
                  </div>
                  <small className="text-muted">Sales, day-close and tax reports for this location use this timezone — no matter where the owner checks from.</small>
                </div>

                {/* Convenience Fee / Processing Fee (card payments only) */}
                <div className="form-group">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="convenienceFeeEnabled"
                      checked={formData.convenienceFeeEnabled}
                      onChange={(e) => handleChange('convenienceFeeEnabled', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="convenienceFeeEnabled">
                      Enable Convenience Fee (Processing Fee)
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Adds an extra % charge on top of card payments only (never on cash) to help cover payment processing costs. Shown to customers as a separate "Convenience Fee" line item.
                  </small>
                </div>

                {formData.convenienceFeeEnabled && (
                  <div className="form-group">
                    <label>Convenience Fee (%)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text"><i className="fas fa-percent"></i></span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.convenienceFeePercent}
                        onChange={(e) => handleChange('convenienceFeePercent', e.target.value)}
                        placeholder="3.00"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>
                    <small className="form-text text-muted">
                      Applied to the subtotal + tax + tip whenever the customer pays by card, from any FodyFee app (POS, handheld, kiosk, online, admin).
                    </small>
                  </div>
                )}

                {/* Points Per Dollar */}
                <div className="form-group">
                  <label>Points Per Dollar</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-coins"></i></span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.pointsPerDollar}
                      onChange={(e) => handleChange('pointsPerDollar', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Fodyes Rewards */}
                <div className="form-group">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="enableFodyesRewards"
                      checked={formData.enableFodyesRewards}
                      onChange={(e) => handleChange('enableFodyesRewards', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="enableFodyesRewards">
                      Enable Fodyes Rewards
                    </label>
                  </div>
                </div>

                {formData.enableFodyesRewards && (
                  <>
                    <div className="form-group">
                      <label>Fodyes Coin Value ($)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-gem"></i></span>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.fodyesCoinValue}
                          onChange={(e) => handleChange('fodyesCoinValue', e.target.value)}
                          placeholder="0.02"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <small className="form-text text-muted">
                        Dollar value of each Fodye coin (e.g. $0.02)
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Fodyes Return Percentage (%)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-percent"></i></span>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.fodyesReturnPercentage}
                          onChange={(e) => handleChange('fodyesReturnPercentage', e.target.value)}
                          placeholder="5.00"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                      <small className="form-text text-muted">
                        % of sale returned to customer as Fodyes (e.g. 5%)
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Max Fodyes Discount ($)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-dollar-sign"></i></span>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.maxFodyesDiscount}
                          onChange={(e) => handleChange('maxFodyesDiscount', e.target.value)}
                          placeholder="10.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <small className="form-text text-muted">
                        Maximum discount this location will accept from Fodyes per order
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Min Order for Fodyes ($)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-shopping-cart"></i></span>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.minOrderForFodyes}
                          onChange={(e) => handleChange('minOrderForFodyes', e.target.value)}
                          placeholder="15.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <small className="form-text text-muted">
                        Minimum order total required before customers can use Fodyes
                      </small>
                    </div>
                  </>
                )}

                {/* Guest Number Restart Point */}
                <div className="form-group">
                  <label>Guest Number Restart Point</label>
                  <select
                    className="form-control"
                    value={formData.ordersCorrelative}
                    onChange={(e) => handleChange('ordersCorrelative', parseInt(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                    <option value={5000}>5000</option>
                    <option value={10000}>10000</option>
                  </select>
                  <small className="form-text text-muted">
                    Guest numbers run 1 up to this value, then restart at 1.
                  </small>
                </div>

                {/* Default Fulfillment Status */}
                <div className="form-group">
                  <label>Default Fulfillment Status</label>
                  <select
                    className="form-control"
                    value={formData.defaultFulfillmentStatus}
                    onChange={(e) => handleChange('defaultFulfillmentStatus', e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="sent_to_kitchen">Sent To Kitchen</option>
                    <option value="in_preparation">In Preparation</option>
                    <option value="ready">Ready</option>
                    <option value="served">Served</option>
                    <option value="out_for_delivery">Out For Delivery</option>
                    <option value="complete">Complete</option>
                  </select>
                  <small className="form-text text-muted">
                    Status applied to items when an order is first created.
                  </small>
                </div>

                {/* Cash Orders Fulfillment Status (override) */}
                <div className="form-group">
                  <label>Cash Orders Fulfillment Status</label>
                  <select
                    className="form-control"
                    value={formData.cashFulfillmentStatus}
                    onChange={(e) => handleChange('cashFulfillmentStatus', e.target.value)}
                  >
                    <option value="pending">Pending (pay first — don't send to kitchen)</option>
                    <option value="sent_to_kitchen">Sent To Kitchen (start preparing immediately)</option>
                  </select>
                  <small className="form-text text-muted">
                    Where CASH orders start (kiosk, online, POS). Choose "Sent To Kitchen" to make them print/appear in the KDS right away — ideal when cash is collected on the spot.
                  </small>
                </div>

                {/* Accept Cash per sales channel (replaces the single global
                    toggle — a restaurant may take cash at the POS drawer but
                    be card-only at the kiosk, etc.) */}
                <div className="form-group">
                  <label>Accept Cash Payments (per sales channel)</label>
                  <div className="p-3 border rounded">
                    {[
                      { key: 'pos', label: 'Point of Sale (POS)', hint: 'native POS payments with the cash drawer' },
                      { key: 'online', label: 'Online orders', hint: 'consumer app / web — pay at pickup or restaurant' },
                      { key: 'kiosk', label: 'Self-service kiosk', hint: 'kiosk checkout cash option' },
                      { key: 'handheld', label: 'Handheld devices', hint: 'cash payments from handhelds' },
                      { key: 'reservation', label: 'Reservations', hint: 'pre-order "pay at restaurant"' },
                    ].map((ch) => (
                      <div className="custom-control custom-switch" key={ch.key}>
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`cash_${ch.key}`}
                          checked={formData.acceptCashChannels[ch.key]}
                          onChange={(e) => handleChange(`cash_${ch.key}`, e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor={`cash_${ch.key}`}>
                          <strong>{ch.label}</strong>
                          <span className="text-muted d-block" style={{ fontSize: '0.78rem' }}>{ch.hint}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="stayTabsOpen"
                      checked={formData.stayTabsOpen}
                      onChange={(e) => handleChange('stayTabsOpen', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="stayTabsOpen">
                      Keep Tabs Open After Payment
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="confirmAllOrders"
                      checked={formData.confirmAllOrders}
                      onChange={(e) => handleChange('confirmAllOrders', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="confirmAllOrders">
                      Confirm All Orders
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Every kiosk/online order (public-web, public-app, kiosk) — cash or card — needs a cashier/manager to
                    accept it on the POS before it goes to the kitchen. Cash orders from those channels already require
                    this regardless of this toggle; native POS/handheld sales are never affected.
                  </small>
                </div>

                {/* Stripe Terminal Tipping Configuration */}
                {isEditMode && stripeLocationId && (
                  <div className="form-group">
                    <div className="card" style={{ borderRadius: 12, border: '1px solid #e0e0e0' }}>
                      <div className="card-header bg-light" style={{ borderRadius: '12px 12px 0 0' }}>
                        <h6 className="m-0 font-weight-bold">
                          <i className="fas fa-credit-card mr-2 text-primary"></i>
                          Stripe Terminal Tipping
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="custom-control custom-switch mb-3">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="stripeTippingEnabled"
                            checked={stripeTippingEnabled}
                            onChange={(e) => setStripeTippingEnabled(e.target.checked)}
                          />
                          <label className="custom-control-label" htmlFor="stripeTippingEnabled">
                            Enable Reader Tipping
                          </label>
                        </div>

                        {stripeTippingEnabled && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.85rem' }}>
                              Tip percentages shown on terminal (exactly 3)
                            </label>
                            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                              {stripeTippingPercentages.map((pct, idx) => (
                                <div key={idx} className="d-flex align-items-center">
                                  <div className="input-group input-group-sm" style={{ width: 120 }}>
                                    <input
                                      type="number"
                                      className="form-control text-center"
                                      value={pct}
                                      min={0}
                                      max={100}
                                      onChange={(e) => handleStripeTippingPercentageChange(idx, e.target.value)}
                                    />
                                    <div className="input-group-append">
                                      <span className="input-group-text">%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-3 text-right">
                          <button
                            className="btn btn-sm btn-primary"
                            style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                            onClick={handleSaveStripeTipping}
                            disabled={stripeTippingLoading}
                          >
                            {stripeTippingLoading ? (
                              <span className="spinner-border spinner-border-sm mr-1"></span>
                            ) : (
                              <i className="fas fa-save mr-1"></i>
                            )}
                            Save Tipping Config
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div className="form-group">
                  <label>Tags</label>
                  <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: -4 }}>
                    Tags help customers discover your restaurant. Add your own or pick from the library.
                  </p>

                  {/* Selected tags */}
                  {(formData.tags || []).length > 0 && (
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 6, marginBottom: 10 }}>
                      {formData.tags.map((slug) => {
                        const predefined = availableTags.find((t) => t.slug === slug);
                        const label = predefined ? predefined.label : slug;
                        return (
                          <span key={slug} className="badge badge-primary" style={{ fontSize: '0.8rem', padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {label}
                            <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: '0.65rem' }} onClick={() => removeTag(slug)}></i>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Add custom tag inline */}
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <div className="input-group input-group-sm" style={{ maxWidth: 240 }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add custom tag..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomTag(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={(e) => {
                            const input = e.target.closest('.input-group').querySelector('input');
                            addCustomTag(input.value);
                            input.value = '';
                          }}
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowTagsModal(true)}
                    >
                      <i className="fas fa-layer-group mr-1"></i>
                      Add Tags
                    </button>
                  </div>
                </div>

                {/* Tags Modal */}
                {showTagsModal && (
                  <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-scrollable modal-lg">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Select Tags</h5>
                          <button type="button" className="close" onClick={() => setShowTagsModal(false)}>
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="modal-body">
                          {(() => {
                            const groups = {};
                            for (const tag of availableTags.length > 0 ? availableTags : FALLBACK_TAGS) {
                              if (!groups[tag.category]) groups[tag.category] = [];
                              groups[tag.category].push(tag);
                            }
                            const catLabels = {
                              cuisines: 'Cuisines',
                              nationalities: 'Nationalities',
                              lifestyle: 'Lifestyle / Dietary',
                              time: 'Time of Day',
                              service: 'Service Model',
                            };
                            const categories = Object.keys(groups).map((key) => ({
                              title: catLabels[key] || key,
                              items: groups[key],
                            }));
                            return categories.map((cat) => (
                              <div key={cat.title} style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>{cat.title}</div>
                                <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                  {cat.items.map((tag) => {
                                    const active = (formData.tags || []).includes(tag.slug);
                                    return (
                                      <button
                                        key={tag.slug}
                                        type="button"
                                        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: 16 }}
                                        onClick={() => toggleTag(tag.slug)}
                                      >
                                        {active && <i className="fas fa-check mr-1"></i>}
                                        {tag.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-primary" onClick={() => setShowTagsModal(false)}>
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Operation Hours */}
                <div className="form-group">
                  <label>Operation Hours</label>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Closed</th>
                          <th>From</th>
                          <th>To</th>
                          <th>+1 Day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.operationHours.map((hours, index) => (
                          <tr key={hours.day}>
                            <td className="text-capitalize">{hours.day}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={hours.isClosed}
                                onChange={(e) => handleOperationHoursChange(index, 'isClosed', e.target.checked)}
                              />
                            </td>
                            <td>
                              <select
                                className="form-control form-control-sm"
                                value={hours.fromHour}
                                onChange={(e) => handleOperationHoursChange(index, 'fromHour', parseInt(e.target.value))}
                                disabled={hours.isClosed}
                              >
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i}>{formatHour12(i)}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-control form-control-sm"
                                value={hours.toHour}
                                onChange={(e) => handleOperationHoursChange(index, 'toHour', parseInt(e.target.value))}
                                disabled={hours.isClosed}
                              >
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i}>{formatHour12(i)}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={hours.isNextDay}
                                onChange={(e) => handleOperationHoursChange(index, 'isNextDay', e.target.checked)}
                                disabled={hours.isClosed}
                                title="Ends next day"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order Types */}
                <div className="form-group">
                  <label>Order Types</label>
                  <div 
                    className="d-flex justify-content-between align-items-center p-3 border rounded"
                    style={{ cursor: isEditMode ? 'pointer' : 'not-allowed', background: isEditMode ? '#fff' : '#f8f9fa' }}
                    onClick={() => isEditMode && setShowOrderTypesModal(true)}
                  >
                    <div>
                      <i className="fas fa-clipboard-list mr-2 text-muted"></i>
                      <span>
                        {orderTypes.filter((t) => t.enabled).length === 0
                          ? 'No order types enabled'
                          : `${orderTypes.filter((t) => t.enabled).length} order type(s) enabled`}
                      </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 4 }}>
                      {orderTypes.map((type) => (
                        // Show enabled order types as active badges and
                        // disabled ones greyed-out/struck-through, so it's
                        // visually obvious which are on vs off — the old
                        // version showed ALL of them identically (confusing).
                        <span
                          key={type.id}
                          className="badge mr-1"
                          style={
                            type.enabled
                              ? { backgroundColor: '#ff6b35', color: '#fff' }
                              : { backgroundColor: '#e0e0e0', color: '#9e9e9e', textDecoration: 'line-through' }
                          }
                        >
                          {type.name}
                        </span>
                      ))}
                      <i className="fas fa-chevron-right ml-2 text-muted"></i>
                    </div>
                  </div>
                  {!isEditMode && (
                    <small className="text-muted">Order types can be added after creating the location</small>
                  )}
                </div>

                {/* Reservations (Dine In) settings — no separate on/off toggle:
                    the "Dine In" order type (Order Types section above) is the
                    single master switch, so the two can never disagree. Placed
                    right after Order Types for context. */}
                <div className="form-group mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12, border: '1px solid #eee' }}>
                  <strong>
                    <i className="fas fa-utensils mr-1 text-muted"></i> Reservations (Dine In)
                  </strong>
                  {dineInEnabled ? (
                    <small className="form-text text-muted d-block">
                      Accepting reservations because the <strong>Dine In</strong> order type is enabled. These settings control how reservations are offered (based on the pattern used by Clover / Toast / OpenTable).
                    </small>
                  ) : (
                    <small className="form-text text-muted d-block">
                      Reservations are <strong>off</strong>. Enable the <strong>Dine In</strong> order type in the Order Types section above to let customers book a table at this location.
                    </small>
                  )}

                  {dineInEnabled && (
                    <div className="mt-3">
                      <div className="row">
                        <div className="col-md-6">
                          <label className="small">Slot interval (minutes)</label>
                          <input type="number" className="form-control" value={formData.reservationSlotInterval}
                            onChange={(e) => handleChange('reservationSlotInterval', e.target.value)} min="5" step="5" />
                          <small className="form-text text-muted">How often slots are offered (e.g. every 30 min)</small>
                        </div>
                        <div className="col-md-6">
                          <label className="small">Table duration (minutes)</label>
                          <input type="number" className="form-control" value={formData.reservationDuration}
                            onChange={(e) => handleChange('reservationDuration', e.target.value)} min="15" step="15" />
                          <small className="form-text text-muted">Average time a table is occupied (turn time)</small>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-6">
                          <label className="small">Min advance booking (hours)</label>
                          <input type="number" className="form-control" value={formData.reservationMinAdvanceHours}
                            onChange={(e) => handleChange('reservationMinAdvanceHours', e.target.value)} min="0" step="1" />
                          <small className="form-text text-muted">Earliest a customer can book ahead</small>
                        </div>
                        <div className="col-md-6">
                          <label className="small">Max advance booking (days)</label>
                          <input type="number" className="form-control" value={formData.reservationMaxAdvanceDays}
                            onChange={(e) => handleChange('reservationMaxAdvanceDays', e.target.value)} min="1" step="1" />
                          <small className="form-text text-muted">How far ahead they can book</small>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-6">
                          <label className="small">Max party size</label>
                          <input type="number" className="form-control" value={formData.reservationMaxPartySize}
                            onChange={(e) => handleChange('reservationMaxPartySize', e.target.value)} min="1" step="1" />
                          <small className="form-text text-muted">Largest group allowed per reservation</small>
                        </div>
                        <div className="col-md-6">
                          <label className="small">Max reservations per slot</label>
                          <input type="number" className="form-control" value={formData.reservationMaxReservationsPerSlot}
                            onChange={(e) => handleChange('reservationMaxReservationsPerSlot', e.target.value)} min="1" step="1" />
                          <small className="form-text text-muted">How many bookings can share the same time slot</small>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-6">
                          <label className="small">No-show grace (minutes)</label>
                          <input type="number" className="form-control" value={formData.reservationAutoNoShowMinutes}
                            onChange={(e) => handleChange('reservationAutoNoShowMinutes', e.target.value)} min="5" step="5" />
                          <small className="form-text text-muted">Auto-mark as no-show this long after start if not seated</small>
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                          <div className="custom-control custom-switch mb-2">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="reservationRequireConfirmation"
                              checked={formData.reservationRequireConfirmation}
                              onChange={(e) => handleChange('reservationRequireConfirmation', e.target.checked)}
                            />
                            <label className="custom-control-label small" htmlFor="reservationRequireConfirmation">
                              Require manual confirmation
                            </label>
                          </div>
                        </div>
                      </div>
                      <small className="form-text text-muted d-block mt-1">
                        If off, reservations confirm automatically. If on, a staff member must approve each one before it's finalized.
                      </small>
                    </div>
                  )}
                </div>

                {/* Buttons */}

                {/* Walk-in Queue settings — for restaurants that take
                    walk-in sign-ups instead of (or alongside) reservations.
                    Independent from Dine In: a location can run the queue
                    without accepting reservations (hybrid model). */}
                <div className="form-group mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12, border: '1px solid #eee' }}>
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="queueEnabled"
                      checked={formData.queueEnabled}
                      onChange={(e) => handleChange('queueEnabled', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="queueEnabled">
                      <strong><i className="fas fa-list-ol mr-1 text-muted"></i> Walk-in Queue (Waitlist)</strong>
                    </label>
                  </div>
                  <small className="form-text text-muted d-block">
                    Let guests join a live waitlist (at the host stand or from the public apps) and get an estimated wait based on occupied tables and the line ahead of them. Works with or without reservations enabled.
                  </small>

                  {formData.queueEnabled && (
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="small">Average table turn (minutes)</label>
                        <input type="number" className="form-control" value={formData.queueAvgTurnMinutes}
                          onChange={(e) => handleChange('queueAvgTurnMinutes', e.target.value)} min="10" step="5" />
                        <small className="form-text text-muted">Used to estimate wait times (e.g. 45)</small>
                      </div>
                      <div className="col-md-6">
                        <label className="small">Max queue size</label>
                        <input type="number" className="form-control" value={formData.queueMaxQueueSize}
                          onChange={(e) => handleChange('queueMaxQueueSize', e.target.value)} min="5" step="5" />
                        <small className="form-text text-muted">Stop new joins when the line gets this long</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery settings — same master-switch pattern as
                    Reservations/Dine In: the "Delivery" order type (Order
                    Types section above) gates this whole section. Includes the
                    coverage zone (radius in miles or ZIP codes, enforced
                    server-side in getUberDeliveryQuote and client-side in the
                    public checkouts) and the fulfillment provider. */}
                <div className="form-group mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12, border: '1px solid #eee' }}>
                  <strong>
                    <i className="fas fa-motorcycle mr-1 text-muted"></i> Delivery
                  </strong>
                  {deliveryEnabled ? (
                    <small className="form-text text-muted d-block">
                      Accepting delivery orders because the <strong>Delivery</strong> order type is enabled. Define where this location delivers.
                    </small>
                  ) : (
                    <small className="form-text text-muted d-block">
                      Delivery is <strong>off</strong>. Enable the <strong>Delivery</strong> order type in the Order Types section above to offer delivery from this location.
                    </small>
                  )}

                  {deliveryEnabled && (
                    <div className="mt-3">
                      {/* Zone type: radius vs zip codes */}
                      <label className="small font-weight-bold d-block">Delivery zone</label>
                      <div className="btn-group btn-group-sm mb-2" role="group">
                        <button
                          type="button"
                          className={`btn ${formData.deliveryZoneType === 'radius' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleChange('deliveryZoneType', 'radius')}
                        >
                          <i className="fas fa-circle-notch mr-1"></i> Radius (miles)
                        </button>
                        <button
                          type="button"
                          className={`btn ${formData.deliveryZoneType === 'zipcodes' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleChange('deliveryZoneType', 'zipcodes')}
                        >
                          <i className="fas fa-mail-bulk mr-1"></i> ZIP codes
                        </button>
                      </div>

                      {formData.deliveryZoneType === 'radius' ? (
                        <div className="row">
                          <div className="col-md-4">
                            <label className="small">Delivery radius (miles)</label>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.deliveryRadiusMiles}
                              onChange={(e) => handleChange('deliveryRadiusMiles', e.target.value)}
                              min="0.5" step="0.5"
                            />
                            <small className="form-text text-muted">
                              Industry standard for restaurant delivery is 3–5 miles (default 5). Customers beyond this distance won't be offered delivery.
                            </small>
                          </div>
                          <div className="col-md-8">
                            <DeliveryZoneMap
                              latitude={formData.latitude}
                              longitude={formData.longitude}
                              radiusMiles={formData.deliveryRadiusMiles}
                            />
                            <small className="form-text text-muted d-block mt-1">
                              <i className="fas fa-map-marker-alt mr-1" style={{ color: '#FF5722' }}></i>
                              The pin is the restaurant; the shaded circle is the area where delivery is offered.
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="small">ZIP codes where delivery is offered</label>
                          <div className="d-flex mb-2" style={{ maxWidth: 360 }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g. 33101"
                              value={formData.deliveryZipInput}
                              onChange={(e) => handleChange('deliveryZipInput', e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const zip = formData.deliveryZipInput.trim();
                                  if (zip && !formData.deliveryZipCodes.includes(zip)) {
                                    handleChange('deliveryZipCodes', [...formData.deliveryZipCodes, zip]);
                                  }
                                  handleChange('deliveryZipInput', '');
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary ml-2"
                              onClick={() => {
                                const zip = formData.deliveryZipInput.trim();
                                if (zip && !formData.deliveryZipCodes.includes(zip)) {
                                  handleChange('deliveryZipCodes', [...formData.deliveryZipCodes, zip]);
                                }
                                handleChange('deliveryZipInput', '');
                              }}
                            >
                              Add
                            </button>
                          </div>
                          <div className="d-flex flex-wrap">
                            {formData.deliveryZipCodes.length === 0 && (
                              <small className="text-muted">No ZIP codes yet — with an empty list the zone is not enforced.</small>
                            )}
                            {formData.deliveryZipCodes.map((zip) => (
                              <span key={zip} className="badge badge-pill badge-light border mr-1 mb-1" style={{ fontSize: '0.85rem', padding: '0.45em 0.7em' }}>
                                {zip}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 ml-1 text-danger"
                                  style={{ fontSize: '0.85rem', lineHeight: 1 }}
                                  onClick={() => handleChange('deliveryZipCodes', formData.deliveryZipCodes.filter((z) => z !== zip))}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </span>
                            ))}
                          </div>
                          <small className="form-text text-muted d-block mt-1">
                            Only customers whose delivery address matches one of these ZIP codes will be offered delivery.
                          </small>
                        </div>
                      )}

                      {/* Fulfillment provider (Uber Direct) */}
                      <hr />
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="deliveryUberEnabled"
                          checked={formData.deliveryUberEnabled}
                          onChange={(e) => handleChange('deliveryUberEnabled', e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="deliveryUberEnabled">
                          <strong>Delivery Fulfillment (Uber Direct)</strong>
                        </label>
                      </div>
                      <small className="form-text text-muted d-block">
                        Delivery orders get a live courier quote at checkout and are dispatched automatically when marked ready in the kitchen. Turn off to handle deliveries yourself.
                      </small>
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <div>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => navigate('/admin/locations')}
                    >
                      Cancel
                    </button>
                    {isEditMode && (
                      <button
                        className="btn btn-danger ml-2"
                        onClick={handleDelete}
                        disabled={saving}
                      >
                        <i className="fas fa-trash mr-2"></i>
                        Delete
                      </button>
                    )}
                  </div>
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <i className="fas fa-save mr-2"></i>
                    {saving ? 'Saving...' : (isEditMode ? 'Update Location' : 'Create Location')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Types Modal */}
      {showOrderTypesModal && isEditMode && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Order Types</h5>
                <button type="button" className="close" onClick={() => setShowOrderTypesModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Standard order types — fixed presets, toggleable */}
                <p className="text-muted small mb-2">Standard order types</p>
                {ORDER_TYPE_PRESETS.map((preset) => {
                  const existing = orderTypes.find((t) => t.key === preset.key);
                  const enabled = existing ? existing.enabled : false;
                  return (
                    <div key={preset.key} className="d-flex justify-content-between align-items-center mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: 10, border: enabled ? '1.5px solid #ff6b35' : '1.5px solid transparent' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{preset.name}</div>
                        <small className="text-muted">
                          {preset.key === 'delivery' && 'Delivery to the customer'}
                          {preset.key === 'pickup' && 'Customer picks up their order'}
                          {preset.key === 'dine_in' && 'Dine-in / reservations'}
                        </small>
                      </div>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`preset-${preset.key}`}
                          checked={enabled}
                          onChange={(e) =>
                            // Use the real doc id if the preset already exists
                            // (legacy ids like `pick_up`), else the canonical
                            // key (for a preset that hasn't been created yet).
                            handleTogglePreset(existing ? existing.id : preset.key, e.target.checked)
                          }
                        />
                        <label className="custom-control-label" htmlFor={`preset-${preset.key}`}></label>
                      </div>
                    </div>
                  );
                })}

                {/* Custom order types — free text, preserved */}
                <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                  <p className="text-muted small mb-0">Custom order types</p>
                  <button className="btn btn-sm btn-outline-primary" onClick={handleAddOrderType}>
                    <i className="fas fa-plus mr-1"></i>Add
                  </button>
                </div>
                {orderTypes.filter((t) => t.isCustom).length === 0 ? (
                  <div className="text-center py-3">
                    <p className="text-muted small mb-0">No custom order types yet</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {orderTypes.filter((t) => t.isCustom).map((type) => (
                      <div key={type.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{type.name}</span>
                        <div>
                          <button
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={() => handleEditOrderType(type.id, type.name)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteOrderType(type.id, type.name)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <small className="text-muted d-block mt-2">
                  Custom order types are used in the POS only — they don't appear as a filter in the consumer apps.
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default LocationMaintenancePage;